import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { ICaptableDataModel } from '../../../data-models/captable2.data-model';
import { isPortfolioCompany } from '../../../data-models/company.data-model';
import { IMetricsResponseDataModel } from '../../../data-models/metrics.data-model';
import { COMPANY_VIEW_TYPE } from '../../../data-models/view-config.data-model';
import { useCompanyMetricsById } from '../../../services/state/CompanyMetricsByIdState';
import { companyStateJ } from '../../../services/state/CompanyStateJ';
import { ColumnMeta } from '../../../types';
import { NumericFormatterId } from '../../../util/formatters/NumericFormatters';
import { columnsByPathState } from '../../PortfolioOverview/state/ViewStateJ';
import { getCaptableSummaryTotals, ICaptableSummaryTotals } from './captable-utils';

export type KpiSource = 'metrics' | 'capTable';

interface BaseKpiConfig {
  key: string;
  path?: string;
  fallbackTitle: string;
  getValue: (data: {
    metricsData: IMetricsResponseDataModel | undefined;
    captableTotals: ICaptableSummaryTotals;
  }) => number | null | undefined;
  format: NumericFormatterId;
  description?: string;
  source: KpiSource;
}

export interface KpiConfig {
  key: string;
  title: string;
  value: number | null | undefined;
  format: NumericFormatterId;
  description?: string;
  source: KpiSource;
}

export interface UseCaptableKpiSummaryResult {
  kpiConfigs: KpiConfig[];
  isLoading: boolean;
}

const MetricsTooltip = 'Calculated from Ledger Data';

const baseKpiConfigs: BaseKpiConfig[] = [
  {
    key: 'fmv',
    path: 'fmv',
    fallbackTitle: 'FMV',
    getValue: ({ metricsData }) => metricsData?.metrics?.[0]?.fmv,
    format: NumericFormatterId.currencyShort,
    description: MetricsTooltip,
    source: 'metrics',
  },
  {
    key: 'totalCost',
    path: 'totalCost',
    fallbackTitle: 'Investment Cost',
    getValue: ({ metricsData }) => metricsData?.metrics?.[0]?.totalCost,
    format: NumericFormatterId.currencyShort,
    description: MetricsTooltip,
    source: 'metrics',
  },
  {
    key: 'lastPostMoney',
    path: 'company.lastPostMoney',
    fallbackTitle: 'Post-Money Valuation',
    getValue: ({ metricsData }) => {
      const companyId = metricsData?.metrics?.[0]?.companyId;
      const company = metricsData?.included?.find((c) => c.id === companyId);
      return company?.lastPostMoney ?? null;
    },
    format: NumericFormatterId.currencyShort,
    description: MetricsTooltip,
    source: 'metrics',
  },
  {
    key: 'clientOwnershipPercentage',
    fallbackTitle: 'Firm Ownership',
    getValue: ({ captableTotals }) => captableTotals.clientOwnershipPercentage,
    format: NumericFormatterId.percent,
    source: 'capTable',
  },
  {
    key: 'totalAmount',
    fallbackTitle: 'Total Raised to Date',
    getValue: ({ captableTotals }) => captableTotals.totalAmount,
    format: NumericFormatterId.currencyShort,
    source: 'capTable',
  },
  {
    key: 'totalFullyDilutedShares',
    fallbackTitle: 'Fully Diluted Shares',
    getValue: ({ captableTotals }) => captableTotals.totalFullyDilutedShares,
    format: NumericFormatterId.numericShort,
    source: 'capTable',
  },
  {
    key: 'totalLiquidationPreference',
    fallbackTitle: 'Total Liquidation Preference',
    getValue: ({ captableTotals }) => captableTotals.totalLiquidationPreference,
    format: NumericFormatterId.currencyShort,
    source: 'capTable',
  },
];

function resolveTitle(config: BaseKpiConfig, columnsByPath: Map<string, ColumnMeta> | null): string {
  if (!config.path || !columnsByPath) {
    return config.fallbackTitle;
  }
  return columnsByPath.get(config.path)?.displayName ?? config.fallbackTitle;
}

function toKpiConfig(
  config: BaseKpiConfig,
  columnsByPath: Map<string, ColumnMeta> | null,
  dataSources: Parameters<BaseKpiConfig['getValue']>[0]
): KpiConfig {
  return {
    key: config.key,
    title: resolveTitle(config, columnsByPath),
    value: config.getValue(dataSources),
    format: config.format,
    description: config.description,
    source: config.source,
  };
}

export function useCaptableKpiSummary(captable: ICaptableDataModel): UseCaptableKpiSummaryResult {
  const company = useAtomValue(companyStateJ(captable.companyId));
  const isPortco = company ? isPortfolioCompany(company) : false;
  const columnsByPath = useAtomValue(columnsByPathState(COMPANY_VIEW_TYPE.PORTFOLIO_OVERVIEW));

  const { data: metricsData, isLoading: isMetricsLoading } = useCompanyMetricsById({
    companyId: captable.companyId,
    asOfDate: captable.date,
    currency: 'usd',
  });

  const captableTotals = useMemo(() => getCaptableSummaryTotals(captable), [captable]);

  const kpiConfigs = useMemo(() => {
    if (!company) {
      return [];
    }

    const dataSources = { metricsData, captableTotals };
    const configs = baseKpiConfigs.map((config) => toKpiConfig(config, columnsByPath, dataSources));

    const visibleConfigs = isPortco ? configs : configs.filter((config) => config.source === 'capTable');
    return visibleConfigs.filter(
      (config) => config.key !== 'totalLiquidationPreference' || config.value != null
    );
  }, [company, isPortco, columnsByPath, metricsData, captableTotals]);

  const isLoading = isPortco && isMetricsLoading;

  return { kpiConfigs, isLoading };
}
